import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, TextField, Typography, Paper, Link, IconButton, InputAdornment } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '../assets/google.svg';

import { signIn, completeNewPassword, cancelNewPasswordChallenge } from "../services/authService";
import { useAuth } from "../../Context/AuthContext";
import { COGNITO_CONFIG, isAdminHost } from "../../Utils/constants";

import { colors, typography, spacing } from '../../styles/theme';
import { authPanelFlexSx, authCloseButtonSx, authInputAutofillSx } from '../styles/authPanel.styles';
import { validatePassword } from '../utils/passwordValidation';
import {
  applyPostLoginRedirect,
  markLoginInProgress,
  peekPostLoginRedirect,
} from '../../Utils/postLoginRedirect';

// Paleta local alineada al mockup (sobre tokens de theme.js)
const loginColors = {
  panelBg: colors.backgroundDark,
  inputBg: '#333333',
  inputBorder: '#4A4A4A',
  textWhite: '#FFFFFF',
  textMuted: colors.textSecondary,
  textSubtitle: '#D4D4D4',
  closeIcon: '#D4D4D4',
  linkAccent: colors.primaryLight,
  buttonDisabledBg: '#525252',
  buttonDisabledText: '#B0B0B0',
  divider: colors.border,
};

const loginTitleSx = {
  fontFamily: typography.fontFamily.heading,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '24px',
  lineHeight: '20px',
  letterSpacing: typography.letterSpacing.tight,
  color: loginColors.textWhite,
  pr: 4,
};

const loginSubtitleSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '20px',
  fontWeight: typography.fontWeight.regular,
  letterSpacing: typography.letterSpacing.tight,
  lineHeight: '20px',
  color: loginColors.textSubtitle,
  mt: '12px',
  mb: 0,
  pr: 2,
};

const loginPanelSx = {
  ...authPanelFlexSx,
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
};

const loginContentSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: '100%',
};

const loginGroupSx = {
  width: '100%',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
};

const loginHeaderSx = {
  ...loginGroupSx,
  flexShrink: 0,
};

const loginMainSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  width: '100%',
  minHeight: 0,
};

const loginFieldsSx = {
  ...loginGroupSx,
  gap: '16px',
};

const loginActionsSx = {
  ...loginGroupSx,
  gap: { xs: 2, sm: 2.5 },
};

const loginLabelSx = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '16px',
  lineHeight: '16px',
  letterSpacing: typography.letterSpacing.wide,
  color: loginColors.textWhite,
  mb: '8px',
  display: 'block',
};

const loginPlaceholderSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  fontWeight: typography.fontWeight.regular,
  lineHeight: '20px',
  letterSpacing: typography.letterSpacing.wide,
  color: loginColors.textMuted,
  opacity: 1,
};

const loginTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    // backgroundColor: loginColors.inputBg,
    borderRadius: spacing.borderRadius.md,
    color: loginColors.textWhite,
    height: 40,
    paddingLeft: '4px',
    '& fieldset': {
      borderColor: loginColors.inputBorder,
    },
    '&:hover fieldset': {
      borderColor: '#666666',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary,
      borderWidth: '2px',
    },
    '&.Mui-disabled': {
      opacity: 1,
      '& fieldset': {
        borderColor: `${loginColors.inputBorder} !important`,
      },
    },
    '&.Mui-disabled:hover fieldset': {
      borderColor: `${loginColors.inputBorder} !important`,
    },
    '&.Mui-error fieldset': {
      borderColor: `${colors.error} !important`,
    },
    '&.Mui-error:hover fieldset': {
      borderColor: `${colors.error} !important`,
    },
    '&.Mui-error.Mui-focused fieldset': {
      borderColor: `${colors.error} !important`,
      borderWidth: '2px',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontFamily: typography.fontFamily.primary,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: typography.fontWeight.regular,
    color: loginColors.textWhite,
    padding: '12px 14px',
    '&::placeholder': loginPlaceholderSx,
    '&::-webkit-input-placeholder': loginPlaceholderSx,
    '&::-moz-placeholder': loginPlaceholderSx,
    '&:-ms-input-placeholder': loginPlaceholderSx,
    ...authInputAutofillSx,
  },
  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: loginColors.textWhite,
    color: loginColors.textWhite,
    opacity: 0.72,
  },
  '& .MuiInputAdornment-root': {
    marginRight: 0,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    opacity: 1,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: 'rgba(255,255,255,0.72)',
    opacity: 0.72,
  },
};

const loginInputIconSx = {
  fontSize: 20,
  color: 'rgba(255,255,255,0.72)',
};

const loginLinkAccentSx = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.regular,
  fontSize: '16px',
  lineHeight: '16px',
  letterSpacing: typography.letterSpacing.wide,
  color: loginColors.linkAccent,
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: '4px',
  border: 'none',
  background: 'none',
  padding: 0,
  '&:hover': {
    color: colors.primary,
  },
};

const loginButtonSx = {
  textTransform: 'none',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  fontSize: '16px',
  lineHeight: '18.41px',
  height: 44,
  borderRadius: spacing.borderRadius.md,
  boxShadow: 'none',
  backgroundColor: colors.primary,
  color: loginColors.textWhite,
  '&:hover': {
    backgroundColor: colors.primaryHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: loginColors.buttonDisabledBg,
    color: loginColors.buttonDisabledText,
  },
};

const loginGoogleButtonSx = {
  textTransform: 'none',
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  fontSize: '16px',
  lineHeight: '18.41px',
  height: 44,
  borderRadius: spacing.borderRadius.md,
  backgroundColor: '#FFFFFF',
  color: '#1A1A1A',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#F5F5F5',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E0E0E0',
    color: '#9E9E9E',
  },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value) => EMAIL_REGEX.test(value.trim());

function LoginOrDivider() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        my: 0,
      }}
    >
      <Box sx={{ flex: 1, height: '1px', backgroundColor: loginColors.divider }} />
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: typography.fontFamily.primary,
            fontSize: '20px',
            fontWeight: typography.fontWeight.regular,
            color: loginColors.textMuted,
            lineHeight: '100%',
            textTransform: 'lowercase',
            width: '16px',
            height: '24px',
          }}
        >
          o
        </Typography>
      </Box>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: loginColors.divider }} />
    </Box>
  );
}

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('login'); // 'login' | 'newPassword'
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const isNewPasswordStep = step === 'newPassword';

  const finishSuccessfulLogin = async (userInfo) => {
    const pendingRedirect = peekPostLoginRedirect();
    await login(userInfo);
    if (applyPostLoginRedirect(pendingRedirect)) return;
    navigate(isAdminHost() ? '/admin' : '/app');
  };

  const resetNewPasswordFields = () => {
    setNewPassword('');
    setConfirmNewPassword('');
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
    setPasswordError('');
    setConfirmPasswordError('');
  };

  const handleBackToLogin = () => {
    cancelNewPasswordChallenge();
    setStep('login');
    resetNewPasswordFields();
    setError('');
    setPassword('');
  };

  const handleUsernameChange = (event) => {
    const value = event.target.value;
    setUsername(value);
    if (emailError && (value.trim() === '' || isValidEmail(value))) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    const trimmed = username.trim();
    if (!trimmed) {
      setEmailError('');
      return;
    }
    setEmailError(isValidEmail(trimmed) ? '' : 'Ingresá un email válido');
  };

  const handleLogin = async () => {
    const trimmedEmail = username.trim();
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Ingresá un email válido');
      return;
    }

    setLoading(true);
    setError("");
    markLoginInProgress(true);

    try {
      const result = await signIn(trimmedEmail, password);

      if (result.success) {
        await finishSuccessfulLogin(result.userInfo);
        return;
      }

      if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        setStep('newPassword');
        resetNewPasswordFields();
        setError('');
        return;
      }

      setError(result.error || "Error al iniciar sesión");
    } catch (err) {
      console.error("Error during login:", err);
      setError(err.message || "Error de conexión");
    } finally {
      markLoginInProgress(false);
      setLoading(false);
    }
  };

  const handleCompleteNewPassword = async () => {
    setError('');
    setPasswordError('');
    setConfirmPasswordError('');

    if (newPassword !== confirmNewPassword) {
      setConfirmPasswordError('Las contraseñas no coinciden');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setLoading(true);
    markLoginInProgress(true);

    try {
      const result = await completeNewPassword(newPassword);

      if (result.success) {
        await finishSuccessfulLogin(result.userInfo);
        return;
      }

      setError(result.error || 'No pudimos actualizar la contraseña. Intentá de nuevo.');
    } catch (err) {
      console.error('Error completing new password challenge:', err);
      setError(err.message || 'Error de conexión');
    } finally {
      markLoginInProgress(false);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    try {
      markLoginInProgress(true);

      const { clientId, cognitoDomain, callbackUrl } = COGNITO_CONFIG;
      const oauthScopes =
        process.env.REACT_APP_COGNITO_OAUTH_SCOPES || "email openid profile";
      const redirectUri = callbackUrl;
      const state = Math.random().toString(36).substring(2, 15) +
                   Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('oauth_state', state);

      const googleAuthUrl =
        `https://${cognitoDomain}/oauth2/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(oauthScopes)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `identity_provider=Google&` +
        `state=${state}&` +
        `prompt=select_account`;

      window.location.href = googleAuthUrl;
    } catch (signInError) {
      console.error('Error iniciando Google Sign-In:', signInError);
      setError('Error al iniciar sesión con Google. Verifica la configuración.');
    }
  };

  const canSubmitNewPassword =
    Boolean(newPassword) && Boolean(confirmNewPassword) && !loading;

  return (
    <Paper elevation={0} sx={loginPanelSx}>
      <IconButton
        size="small"
        onClick={() => navigate(isAdminHost() ? '/admin' : '/app')}
        aria-label="Cerrar"
        sx={authCloseButtonSx}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={loginContentSx}>
        <Box sx={loginHeaderSx}>
          <Typography component="h1" sx={loginTitleSx}>
            {isNewPasswordStep ? 'Elegí tu contraseña' : 'Te damos la bienvenida'}
          </Typography>
          <Typography component="p" sx={loginSubtitleSx}>
            {isNewPasswordStep
              ? 'Tu cuenta tiene una contraseña temporal. Definí una permanente para continuar.'
              : 'Inicia sesión para acceder a todas las funcionalidades.'}
          </Typography>
        </Box>

        <Box sx={loginMainSx}>
        {isNewPasswordStep ? (
          <>
            <Box sx={loginFieldsSx}>
              <Box>
                <Typography component="label" sx={loginLabelSx}>
                  Nueva contraseña
                </Typography>
                <TextField
                  placeholder="Ingresá tu nueva contraseña"
                  variant="outlined"
                  fullWidth
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                    if (error) setError('');
                  }}
                  disabled={loading}
                  autoComplete="new-password"
                  error={!!passwordError}
                  sx={loginTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                        <LockOutlinedIcon sx={loginInputIconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: '4px' }}>
                        <IconButton
                          aria-label={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          onClick={() => setShowNewPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                          disabled={loading}
                          sx={{
                            color: 'rgba(255,255,255,0.72)',
                            '&:hover': { backgroundColor: 'transparent', color: loginColors.textWhite },
                          }}
                        >
                          {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {passwordError && (
                  <Typography
                    sx={{
                      fontSize: typography.fontSize.xs,
                      fontFamily: typography.fontFamily.primary,
                      fontWeight: typography.fontWeight.regular,
                      color: colors.error,
                      mt: '6px',
                      textAlign: 'left',
                      lineHeight: typography.lineHeight.tight,
                    }}
                  >
                    {passwordError}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography component="label" sx={loginLabelSx}>
                  Confirmar contraseña
                </Typography>
                <TextField
                  placeholder="Repetí tu nueva contraseña"
                  variant="outlined"
                  fullWidth
                  type={showConfirmNewPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => {
                    setConfirmNewPassword(e.target.value);
                    if (confirmPasswordError) setConfirmPasswordError('');
                    if (error) setError('');
                  }}
                  disabled={loading}
                  autoComplete="new-password"
                  error={!!confirmPasswordError}
                  sx={loginTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                        <LockOutlinedIcon sx={loginInputIconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: '4px' }}>
                        <IconButton
                          aria-label={showConfirmNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          onClick={() => setShowConfirmNewPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                          disabled={loading}
                          sx={{
                            color: 'rgba(255,255,255,0.72)',
                            '&:hover': { backgroundColor: 'transparent', color: loginColors.textWhite },
                          }}
                        >
                          {showConfirmNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {(confirmPasswordError || error) && (
                  <Typography
                    sx={{
                      fontSize: typography.fontSize.xs,
                      fontFamily: typography.fontFamily.primary,
                      fontWeight: typography.fontWeight.regular,
                      color: colors.error,
                      mt: '6px',
                      textAlign: 'left',
                      lineHeight: typography.lineHeight.tight,
                    }}
                  >
                    {confirmPasswordError || error}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              component="form"
              onSubmit={(e) => { e.preventDefault(); handleCompleteNewPassword(); }}
              sx={loginActionsSx}
            >
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={!canSubmitNewPassword}
                sx={loginButtonSx}
              >
                {loading ? "Guardando..." : "Continuar"}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={handleBackToLogin}
                  disabled={loading}
                  sx={loginLinkAccentSx}
                >
                  Volver al inicio de sesión
                </Link>
              </Box>
            </Box>
          </>
        ) : (
          <>
        <Box sx={loginFieldsSx}>
          <Box>
            <Typography component="label" sx={loginLabelSx}>
              Email o usuario
            </Typography>
            <TextField
              placeholder="usuario@email.com"
              variant="outlined"
              fullWidth
              value={username}
              onChange={handleUsernameChange}
              onBlur={handleEmailBlur}
              disabled={loading}
              autoComplete="email"
              error={!!emailError}
              sx={loginTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                    <MailOutlineIcon sx={loginInputIconSx} />
                  </InputAdornment>
                ),
              }}
            />
            {emailError && (
              <Typography
                sx={{
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                  fontWeight: typography.fontWeight.regular,
                  color: colors.error,
                  mt: '6px',
                  textAlign: 'left',
                  lineHeight: typography.lineHeight.tight,
                }}
              >
                {emailError}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography component="label" sx={loginLabelSx}>
              Contraseña
            </Typography>
            <TextField
              placeholder="Ingresá tu contraseña"
              variant="outlined"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              sx={loginTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                    <LockOutlinedIcon sx={loginInputIconSx} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: '4px' }}>
                    <IconButton
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      onClick={() => setShowPassword((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                      disabled={loading}
                      sx={{
                        color: 'rgba(255,255,255,0.72)',
                        '&:hover': { backgroundColor: 'transparent', color: loginColors.textWhite },
                      }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Typography
                sx={{
                  fontSize: typography.fontSize.xs,
                  fontFamily: typography.fontFamily.primary,
                  fontWeight: typography.fontWeight.regular,
                  color: colors.error,
                  mt: '6px',
                  textAlign: 'left',
                  lineHeight: typography.lineHeight.tight,
                }}
              >
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '14px' }}>
              <Link
                component="button"
                type="button"
                onClick={() => navigate("/forgot-password")}
                sx={loginLinkAccentSx}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
          sx={loginActionsSx}
        >
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={!username || !password || loading || !!emailError}
            sx={loginButtonSx}
          >
            {loading ? "Iniciando sesión..." : "Iniciar sesión"}
          </Button>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '4px',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: typography.fontFamily.primary,
                fontSize: '16px',
                fontWeight: typography.fontWeight.regular,
                color: loginColors.textWhite,
                lineHeight: '16px',
              }}
            >
              ¿No tienes una cuenta?
            </Typography>
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/register")}
              sx={loginLinkAccentSx}
            >
              Crear cuenta
            </Link>
          </Box>

          <LoginOrDivider />

          <Button
            type="button"
            variant="contained"
            fullWidth
            disableElevation
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<img src={GoogleIcon} alt="Google" width={18} height={18} />}
            sx={loginGoogleButtonSx}
          >
            Continuar con Google
          </Button>
        </Box>
          </>
        )}
        </Box>
      </Box>
    </Paper>
  );
};

export default Login;
